import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Icon, Select, TextArea } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../Localization';
import { ConversationItemWithAKMeta } from '@aidkitorg/types/lib/convoai/conversation';
import { useAICoach } from './AICoachContext';

export const FeedbackButtons = React.memo((props: ConversationItemWithAKMeta["meta"]["feedback"] & { id: string }) => {
  const { chatChannel } = useAICoach();
  const L = useLocalizedStrings();
  const [additionalFeedbackInput, setAdditionalFeedbackInput] = useState<string>(props.additionalFeedback ?? "");
  const [showAdditional, setShowAdditional] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const { id, ...feedback } = props;
    
  const FeedbackReasons = [
    { value: '', i18nKey: "feedback_select_reason" },
    { value: 'too_slow', i18nKey: "feedback_too_slow" },
    { value: 'not_accurate', i18nKey: "feedback_not_accurate" },
    { value: 'tone', i18nKey: "feedback_tone" },
    { value: 'other', i18nKey: "feedback_other" }
  ].map(
    option => ({
      value: option.value,
      label: L.ai_coach[option.i18nKey as keyof Omit<typeof L.ai_coach, 'phases'>]
    })
  );
    
  const selectedReason = useMemo(() => {
    return FeedbackReasons.find(r => r.value === props.reason) ?? FeedbackReasons[0];
  }, [props.reason]);
    
  if (!chatChannel) {
    return null;
  }
    
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm">
        <button
          className={`${feedback.rating === "up" ? "text-indigo-600 opacity-100" : "text-gray-600 opacity-75"} hover:opacity-100 transition ease-in-out duration-100`}
          onClick={() => {
            chatChannel.dispatch({
              type: "update-item-meta",
              id,
              update: {
                feedback: {
                  ...feedback,
                  reason: null,
                  additionalFeedback: null,
                  rating: props.rating === "up" ? undefined : "up"
                }
              }
            });
            setShowSuccess(true);
          }}>
          <Icon size="md" name="HandThumbUpIcon" type="solid" />
        </button>
        
        <button
          className={`${feedback.rating === "down" ? "text-indigo-600 opacity-100" : "text-gray-600 opacity-75"} hover:opacity-100 transition ease-in-out duration-100`}
          onClick={() => {
            const newRating = props.rating === "down" ? undefined : "down";
            chatChannel.dispatch({
              type: "update-item-meta",
              id,
              update: {
                feedback: {
                  ...feedback,
                  rating: newRating
                }
              }
            });
            setShowAdditional(newRating === "down");
          }}
        >
          <Icon size="md" name="HandThumbDownIcon" type="solid" />
        </button>
        <div>{L.ai_coach.feedback_was_this_helpful}</div>
      </div>
      
      {(feedback.rating === "down" && showAdditional) && (
        <>
          <Select
            label={L.ai_coach.feedback_tell_us_more}
            labelLocation="above"
            anchor="top"
            description={L.ai_coach.feedback_select_reason}
            options={selectedReason ? FeedbackReasons.slice(1) : FeedbackReasons}
            value={selectedReason}
            onChange={(option) => {
              chatChannel.dispatch({
                type: "update-item-meta",
                id,
                update: {
                  feedback: {
                    ...feedback,
                    reason: option.value
                  }
                }
              });
            }}
          />
          {props.reason === 'other' && <form 
            className="flex flex-col gap-2"
            onSubmit={
              e => { 
                e.preventDefault();
                chatChannel.dispatch({
                  type: "update-item-meta",
                  id,
                  update: {
                    feedback: {
                      ...feedback,
                      additionalFeedback: additionalFeedbackInput
                    }
                  }
                });
                setShowSuccess(true)
              }
            }>
            <TextArea
              rows={2}
              label={L.ai_coach.feedback_additional}
              value={additionalFeedbackInput}
              onChange={e => setAdditionalFeedbackInput(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary"
                className="self-start"
                size="sm"
                disabled={!additionalFeedbackInput}
                type="submit"
              >
                {L.applicant.meeting.submit}
              </Button>
              {(props.rating === "down" && props.reason && props.additionalFeedback && showSuccess) && <div className="text-green-600 text-sm">{L.ai_coach.feedback_thanks}</div>}
            </div>
          </form>}          
        </>
      )}
      {(props.rating === "up" && showSuccess) && <div className="text-green-600 text-sm">{L.ai_coach.feedback_thanks}</div>}
    </div>
  );
});
